import { Input } from '@heidi/design-system/input';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

import type { BodyAspect, OnsetSpeed } from './VisualDxDiagnosticFlow.constants';
import {
  segmentedButtonClass,
  segmentedWrapClass,
} from './VisualDxDiagnosticFlow.styles';

/**
 * Tiny presentational leaves shared across the diagnostic-flow steps.
 *
 * Kept in their own file so step components stay readable and the parent
 * flow component stays under the 300-line linter cap.
 */

// `<dt>` / `<dd>` are semantically required for the description-list inside
// `ReviewSummary`, and the design system's Typography components are
// hardcoded to `<p>`. Apply the same `typographyVariant` cva classes
// directly so font sizing/weight stays token-driven (no inline font
// styles, no raw `text-xs` violations).
export const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between py-1.5">
    <dt className={cn(typographyVariant({ variant: 'caption' }), 'text-text-secondary')}>
      {label}
    </dt>
    <dd
      className={cn(
        typographyVariant({ variant: 'captionBold' }),
        'm-0 text-right text-text-primary'
      )}
    >
      {value}
    </dd>
  </div>
);

/** Front/Back segmented control at the top of the region step. */
export const FrontBackToggle = ({
  value,
  onChange,
}: {
  value: BodyAspect;
  onChange: (next: BodyAspect) => void;
}) => (
  <div role="tablist" aria-label="Body aspect" className={segmentedWrapClass}>
    <button
      type="button"
      role="tab"
      aria-selected={value === 'front'}
      onClick={() => onChange('front')}
      className={segmentedButtonClass(value === 'front')}
    >
      Front
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={value === 'back'}
      onClick={() => onChange('back')}
      className={segmentedButtonClass(value === 'back')}
    >
      Back
    </button>
  </div>
);

/** Numeric age input for the age step. */
export const AgeNumberInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) => (
  <Input
    id="age-input"
    type="number"
    min={0}
    max={120}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder="34"
    aria-label="Exact age in years"
    className="w-20"
  />
);

// ── Onset line-graph icons (24x16 viewBox, themed via currentColor) ────

const onsetIconStrokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Stylised line-graph icon for the onset-speed cards. Uses
 *  `text-validation-success` so it inherits the design system's success
 *  green tone (themable for `newBrandDark`). Width fixed at 28px to
 *  match the demo's left-rail icon column. */
export const OnsetIcon = ({ kind }: { kind: OnsetSpeed }) => {
  const className = cn('text-validation-success');
  switch (kind) {
    case 'rapidly':
      return (
        <svg className={className} viewBox="0 0 24 16" width={28} height={18} aria-hidden>
          {/* near-vertical jump up */}
          <path d="M 4 14 L 4 4 L 20 4" {...onsetIconStrokeProps} />
        </svg>
      );
    case 'acutely':
      return (
        <svg className={className} viewBox="0 0 24 16" width={28} height={18} aria-hidden>
          {/* steady rise */}
          <path d="M 4 13 L 20 3" {...onsetIconStrokeProps} />
        </svg>
      );
    case 'chronically':
      return (
        <svg className={className} viewBox="0 0 24 16" width={28} height={18} aria-hidden>
          {/* gradual curve up */}
          <path d="M 4 13 Q 14 13 20 4" {...onsetIconStrokeProps} />
        </svg>
      );
    case 'recurring':
      return (
        <svg className={className} viewBox="0 0 24 16" width={28} height={18} aria-hidden>
          {/* sawtooth */}
          <path
            d="M 3 12 L 7 5 L 11 12 L 15 5 L 19 12 L 21 8"
            {...onsetIconStrokeProps}
          />
        </svg>
      );
    case 'presentAtBirth':
      return (
        <svg className={className} viewBox="0 0 24 16" width={28} height={18} aria-hidden>
          {/* flat line */}
          <path d="M 4 8 L 20 8" {...onsetIconStrokeProps} />
        </svg>
      );
  }
};

// Note on `FrontBackToggle`: the segmented control above uses a raw
// `<button role="tab">` because it is rendered inside `role="tablist"`
// — the design-system `<Button>` doesn't expose role/aria-selected props
// in a way that preserves the tab semantics, so the raw element is
// preferred here for correct WAI-ARIA compliance. All visual styling
// still flows through token-based className helpers
// (`segmentedButtonClass`, `segmentedWrapClass`).
